import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Shield, AlertTriangle, ScrollText, ArrowRight } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import api from '@/lib/api';
import type { ApiResponse } from '@hirenfire/shared';

interface AdminStats {
  totalUsers: number;
  pendingKyc: number;
  activeDisputes: number;
  totalEscrowHeld: number;
}

interface AuditEntry {
  id: string;
  action: string;
  actor: string;
  target: string;
  createdAt: string;
}

export function AdminDashboard() {
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    pendingKyc: 0,
    activeDisputes: 0,
    totalEscrowHeld: 0,
  });
  const [auditLogs, setAuditLogs] = useState<AuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [statsRes, auditRes] = await Promise.all([
          api.get<ApiResponse<AdminStats>>('/admin/stats'),
          api.get<ApiResponse<AuditEntry[]>>('/admin/audit?limit=10'),
        ]);
        if (statsRes.data.data) setStats(statsRes.data.data);
        if (auditRes.data.data) setAuditLogs(auditRes.data.data);
      } catch {
        // fallback
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
        <p className="text-gray-400 mt-1">Platform overview and management</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Total Users</p>
                <p className="text-2xl font-bold text-white">{stats.totalUsers}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-warning-500/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-warning-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Pending KYC</p>
                <p className="text-2xl font-bold text-white">{stats.pendingKyc}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-error-500/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-error-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Active Disputes</p>
                <p className="text-2xl font-bold text-white">{stats.activeDisputes}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-success-500/10 flex items-center justify-center">
                <ScrollText className="w-6 h-6 text-success-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Escrow Held</p>
                <p className="text-2xl font-bold text-white">
                  ${stats.totalEscrowHeld.toLocaleString()}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader action={
            <Link to="/admin/kyc">
              <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
                Review All
              </Button>
            </Link>
          }>
            Quick Actions
          </CardHeader>
          <CardBody className="space-y-3">
            <Link
              to="/admin/kyc"
              className="flex items-center justify-between p-4 rounded-lg bg-dark-700/50 hover:bg-dark-700 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-warning-400" />
                <span className="text-white">Review Pending KYC</span>
              </div>
              <Badge variant="warning">{stats.pendingKyc}</Badge>
            </Link>
            <Link
              to="/admin/escrow"
              className="flex items-center justify-between p-4 rounded-lg bg-dark-700/50 hover:bg-dark-700 transition-colors"
            >
              <div className="flex items-center gap-3">
                <ScrollText className="w-5 h-5 text-primary-400" />
                <span className="text-white">Manage Escrow</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-500" />
            </Link>
            <Link
              to="/admin/bans"
              className="flex items-center justify-between p-4 rounded-lg bg-dark-700/50 hover:bg-dark-700 transition-colors"
            >
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-error-400" />
                <span className="text-white">Manage Bans</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-500" />
            </Link>
          </CardBody>
        </Card>

        <Card>
          <CardHeader action={
            <Link to="/admin/audit">
              <Button variant="ghost" size="sm" rightIcon={<ArrowRight className="w-4 h-4" />}>
                View All
              </Button>
            </Link>
          }>
            Recent Audit Log
          </CardHeader>
          <CardBody>
            {auditLogs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No audit entries</p>
            ) : (
              <div className="space-y-2">
                {auditLogs.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-dark-700/50 text-sm"
                  >
                    <div>
                      <span className="text-white font-medium">{entry.action}</span>
                      <span className="text-gray-400"> by {entry.actor}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(entry.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
